import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import CategorySelect from "../../../components/selectCategory/SelectCategory";
import DescriptionEditor from "../../../components/descriptionEditor/DescriptionEditor";

const baseURL = process.env.REACT_APP_API_BASE_URL;

const CreateProduct = () => {
  const [product, setProduct] = useState({
    title: "",
    price: "",
    discountPrice: "",
  });
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProduct((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDescriptionChange = (htmlContent) => {
    setDescription(htmlContent);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      // Get the token from session storage
      const token = sessionStorage.getItem("token");

      if (!token) {
        throw new Error("Authentication token not found. Please login.");
      }

      // Format the product data
      const productData = {
        title: product.title,
        description: description,
        price: product.price,
        discountPrice: product.discountPrice,
        categoryId: parseInt(categoryId) || 0,
      };

      // Make the API call
      const response = await axios.post(
        `${baseURL}/api/products/`,
        productData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
    
      const productId = response?.data?.productId;

      navigate(`/admin/product/edit/${productId}`);

      setSuccess(true);
      // Reset form after successful submission
      setProduct({
        title: "",
        price: "",
        discountPrice: "",
      });
      setDescription("");
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to create product"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-0  sm:mx-auto  p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6 border-b pb-3">Create New Product</h2>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded">
          <p className="text-red-700">{error}</p>
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6 rounded">
          <p className="text-green-700">Product created successfully!</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="form-group">
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
            Product Title
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={product.title}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 shadow-sm"
            placeholder="Enter product title"
          />
        </div>

        <div className="form-group">
          <label htmlFor="categoryId" className="block text-sm font-medium text-gray-700 mb-1">
            Category ID
          </label>
          <CategorySelect onCategorySelect={setCategoryId} />
        </div>

        <div className="form-group">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Product Description
          </label>
          <DescriptionEditor onChange={handleDescriptionChange} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="form-group">
            <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
              Price ($)
            </label>
            <input
              type="text"
              id="price"
              name="price"
              value={product.price}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 shadow-sm"
              placeholder="0.00"
            />
          </div>

          <div className="form-group">
            <label htmlFor="discountPrice" className="block text-sm font-medium text-gray-700 mb-1">
              Discount Price ($)
            </label>
            <input
              type="text"
              id="discountPrice"
              name="discountPrice"
              value={product.discountPrice}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 shadow-sm"
              placeholder="0.00 (optional)"
            />
          </div>
        </div>

        <div className="pt-3">
          <button
            type="submit"
            disabled={loading}
            className={`w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-md shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              loading ? "opacity-75 cursor-not-allowed" : ""
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating Product...
              </span>
            ) : (
              "Create Product"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateProduct;